import React, { useEffect, useState } from "react";
import { profile, mitreTechniques } from "../mock";
import { ArrowRight, Github, Linkedin, ChevronDown, Activity } from "lucide-react";

const terminalLines = [
  { p: "analyst@soc:~$", cmd: "./initialize_soc.sh" },
  { p: "", cmd: "", out: "[\u2713] Global SOC Session Authenticated" },
  { p: "analyst@soc:~$", cmd: "splunk monitor --live --index=security" },
  { p: "", cmd: "", out: "[\u26a1] Ingesting Endpoint + Email + Cloud Telemetry..." },
  { p: "", cmd: "", out: "[\u26a1] Correlating Proofpoint \u2192 SentinelOne \u2192 Wiz" },
  { p: "", cmd: "", out: "[!] ALERT: Phishing Cluster Detected \u2014 7 mailboxes", cls: "text-amber-400" },
  { p: "", cmd: "", out: "[\u2192] Enriching IOCs via ThreatQ \u2192 T1566.002", cls: "text-cyan-400" },
  { p: "", cmd: "", out: "[\u2713] Contained. Bridge Call Opened. L2 Documented.", cls: "text-emerald-400" },
];

const Hero = () => {
  const [visible, setVisible] = useState(0);

  useEffect(() => {
    if (visible >= terminalLines.length) return;
    const t = setTimeout(() => setVisible((v) => v + 1), 600);
    return () => clearTimeout(t);
  }, [visible]);

  return (
    <section id="about" className="relative pt-28 pb-20 px-6 lg:px-10 overflow-hidden">
      <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
      <div className="absolute inset-0 vignette pointer-events-none" />

      <div className="relative max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 border border-[#1c2533] bg-[#0c1118] px-3 py-1.5 rounded-sm mb-7">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot" />
            <span className="font-mono text-[10px] tracking-[0.22em] text-slate-400">
              {profile.classification}
            </span>
          </div>

          <h1 className="font-mono font-bold text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight mb-6 glow-cyan">
            AVADHUT
            <br />
            <span className="text-cyan-400">LOHAR</span>
            <span className="text-cyan-400 cursor-blink">_</span>
          </h1>

          <p className="font-mono text-xs md:text-sm tracking-[0.2em] text-slate-400 mb-6">
            SOC ANALYST <span className="text-cyan-500/60">//</span> INCIDENT RESPONDER{" "}
            <span className="text-cyan-500/60">//</span> THREAT HUNTER
          </p>

          <p className="text-slate-300 text-base md:text-lg leading-relaxed max-w-xl mb-9">
            {profile.tagline}
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <a
              href="#experience"
              className="group inline-flex items-center gap-2.5 bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-mono font-semibold text-xs tracking-[0.18em] px-5 py-3 rounded-sm transition-colors"
            >
              VIEW EXPERIENCE
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href={profile.github}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 border border-[#2a3a52] hover:border-cyan-400 hover:text-cyan-400 text-slate-200 font-mono text-xs tracking-[0.18em] px-5 py-3 rounded-sm transition-colors"
            >
              <Github size={14} /> GITHUB
            </a>
            <a
              href={profile.linkedin}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 border border-[#2a3a52] hover:border-cyan-400 hover:text-cyan-400 text-slate-200 font-mono text-xs tracking-[0.18em] px-5 py-3 rounded-sm transition-colors"
            >
              <Linkedin size={14} /> LINKEDIN
            </a>
          </div>

          <div className="flex flex-wrap gap-1.5 mt-9 max-w-xl">
            {mitreTechniques.slice(0, 8).map((m) => (
              <span
                key={m.id}
                className="font-mono text-[10px] tracking-[0.1em] text-slate-300 border border-[#1c2533] bg-[#0c1118] px-2 py-1 rounded-sm hover:border-cyan-400 hover:text-cyan-400 transition-colors"
              >
                <span className="text-cyan-400 mr-1.5">{m.id}</span>
                {m.name}
              </span>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="terminal-window shadow-[0_0_60px_-20px_rgba(34,211,238,0.35)] corner-brackets">
            <div className="terminal-header">
              <span className="term-dot bg-red-500/80" />
              <span className="term-dot bg-amber-400/80" />
              <span className="term-dot bg-emerald-400/80" />
              <span className="font-mono text-[10px] tracking-[0.18em] text-slate-500 ml-3">
                soc-terminal &mdash; bash &mdash; 80x24
              </span>
              <span className="ml-auto flex items-center gap-1.5 font-mono text-[10px] tracking-[0.15em] text-emerald-400">
                <Activity size={11} /> LIVE
              </span>
            </div>
            <div className="p-5 font-mono text-[12.5px] leading-[1.85] min-h-[360px] relative">
              {terminalLines.slice(0, visible).map((l, i) => (
                <div key={i} className="fade-up">
                  {l.p ? (
                    <div>
                      <span className="text-emerald-400">{l.p}</span>{" "}
                      <span className="text-slate-100">{l.cmd}</span>
                    </div>
                  ) : (
                    <div className={l.cls || "text-slate-300"}>{l.out}</div>
                  )}
                </div>
              ))}
              {visible >= terminalLines.length && (
                <div className="text-emerald-400">
                  analyst@soc:~$ <span className="cursor-blink text-cyan-400">_</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <a
        href="#about-detail"
        className="hidden md:flex absolute bottom-4 left-1/2 -translate-x-1/2 flex-col items-center gap-1 text-slate-500 hover:text-cyan-400 transition-colors"
      >
        <span className="font-mono text-[9px] tracking-[0.3em]">SCROLL</span>
        <ChevronDown size={14} className="animate-bounce" />
      </a>
    </section>
  );
};

export default Hero;
